import { FC, useEffect } from "react";
import clsx from "classnames";
import { useTranslation } from "react-i18next";
import { InventoryAvatarData } from "@onmo/graphql/graphql";
import { Dialog } from "../../core/Dialog/Dialog";
import { Icon } from "../../core/Icon/Icon";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import ArrowWrapper from "../ArrowWrapper/ArrowWrapper";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { useFetchFreeAvatars } from "../../../hooks/useSelectAvatarQuery";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isGamepadMode } from "../../../hooks/useCrossInput";
import { isTV } from "../../../utils/platform";
import { getLocalizedField } from "../../../models/localizedField";
import { useStyles } from "./ChooseAvatarPopup.style";

interface IChooseAvatarPopupProps {
  open: boolean;
  selectedId?: string;
  onSave: (inventoryId: string, url: string) => void;
  onClose: () => void;
}

export const ChooseAvatarPopup: FC<IChooseAvatarPopupProps> = ({
  open,
  selectedId,
  onSave,
  onClose,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { avatarBundles } = useFetchFreeAvatars();
  const { setActiveElement } = useGamepadContext();

  // The highlight starts on the first avatar of the row
  const focusId = avatarBundles.find((bundle) => bundle.avatars.length)
    ?.avatars[0]?.id;

  useEffect(() => {
    if (!open || !focusId) return;
    const frame = requestAnimationFrame(() => {
      if (!isGamepadMode() && !isTV) {
        document
          .querySelectorAll<HTMLElement>('[data-testid="choose-avatar-list"]')
          .forEach((list) => (list.scrollLeft = 0));
        return;
      }
      const target = document.querySelector<HTMLElement>(
        'dialog [data-gamepad="default-selected"]',
      );
      if (!target || target === document.activeElement) return;
      setActiveElement(target);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, focusId, setActiveElement]);

  const title = t("ChooseAvatar_Choose Your Avatar");

  return (
    <Dialog
      open={open}
      hideLayout
      title={title}
      data-dialog-nav="rows"
      className={classes.page}
      onClose={onClose}
    >
      <div className={classes.header}>
        <div
          className={classes.backButton}
          role="button"
          tabIndex={-1}
          aria-label={t("A11y_Back")}
          onClick={onClose}
          data-gamepad="choose-avatar-back"
          data-selected-class={classes.backButtonSelected}
          data-button-sound="back"
        >
          <Icon
            name="icon-linear-arrow-left-3"
            size={24}
            className={classes.backIcon}
          />
        </div>
        <Typography
          variant={TypographyVariant.HeadingH3Bold}
          className={classes.title}
          role="heading"
          aria-level={1}
        >
          {title}
        </Typography>
      </div>
      <div className={classes.bundleList}>
        {avatarBundles.map((bundle) => {
          const bundleTitle =
            getLocalizedField(bundle.metadata, "title", i18n.language) ||
            bundle.title;
          return (
            <div key={bundle.id} className={classes.bundle}>
              <Typography variant={TypographyVariant.BodyB4Bold}>
                {bundleTitle}
              </Typography>
              <ArrowWrapper
                className={classes.avatarList}
                size={16}
                showWhenScrollable
                role="radiogroup"
                aria-label={bundleTitle}
                data-testid="choose-avatar-list"
              >
                {bundle.avatars.map((avatar, i) => {
                  const url = (avatar.data as InventoryAvatarData)?.url;
                  return (
                    <div
                      key={avatar.id}
                      className={clsx(classes.avatar, {
                        [classes.avatarActive]: avatar.id === selectedId,
                      })}
                      role="radio"
                      tabIndex={-1}
                      aria-checked={avatar.id === selectedId}
                      aria-label={t("A11y_Avatar number", { number: i + 1 })}
                      onClick={() => onSave(avatar.id, url)}
                      data-gamepad={
                        avatar.id === focusId
                          ? "default-selected"
                          : `choose-avatar-${avatar.id}`
                      }
                      data-selected-class={classes.avatarSelected}
                    >
                      <StaticImage
                        alt=""
                        className={classes.avatarImage}
                        src={url}
                      />
                    </div>
                  );
                })}
              </ArrowWrapper>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};
